import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, Check, Trash2, Star, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GradientButton } from "@/screens/_shared";
import {
    addPayoutMethod,
    getPayoutMethods,
    mockAccountNameLookup,
    NG_BANKS,
    removePayoutMethod,
    setPrimaryPayout,
} from "@/lib/monetizationStore";
import { useMoneVersion } from "@/lib/hooks";
import { useMemo } from "react";

const PayoutMethods = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useMoneVersion();
    const [adding, setAdding] = useState(false);

    const methods = useMemo(() => (user ? getPayoutMethods(user.id) : []), [user, v]);

    return (
        <div className="screen" data-testid="payouts-screen">
            <PageHeader title="Payout Methods" />
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
                {methods.length === 0 && !adding && (
                    <div className="text-center pt-6" data-testid="payouts-empty">
                        <span className="mx-auto h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                            <Banknote size={22} />
                        </span>
                        <h2 className="mt-4 font-display text-[17px] font-semibold">No bank account yet</h2>
                        <p className="mt-1 text-[12.5px] text-muted-foreground">
                            Add a bank account to start receiving payouts from your bookings.
                        </p>
                    </div>
                )}

                {methods.length > 0 && (
                    <ul className="space-y-2.5" data-testid="payouts-list">
                        {methods.map((m) => (
                            <li
                                key={m.id}
                                data-testid={`payout-row-${m.id}`}
                                className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card"
                            >
                                <span
                                    className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-semibold text-[11px]"
                                    style={{ background: m.bankColor }}
                                >
                                    {m.bankName.slice(0, 2).toUpperCase()}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[13.5px] font-semibold truncate flex items-center gap-1.5">
                                        {m.accountName}
                                        {m.isPrimary && (
                                            <span className="inline-flex items-center gap-0.5 text-[10px] text-primary font-semibold">
                                                <Star size={11} className="fill-primary text-primary" /> Primary
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11.5px] text-muted-foreground truncate">
                                        {m.bankName} · {m.accountNumber}
                                    </div>
                                </div>
                                {!m.isPrimary && (
                                    <button
                                        data-testid={`payout-primary-${m.id}`}
                                        onClick={() => setPrimaryPayout(user.id, m.id)}
                                        className="h-9 px-3 rounded-lg bg-secondary text-[11.5px] font-medium hover:bg-muted"
                                    >
                                        Set primary
                                    </button>
                                )}
                                <button
                                    data-testid={`payout-remove-${m.id}`}
                                    onClick={() => removePayoutMethod(user.id, m.id)}
                                    className="h-9 w-9 rounded-lg text-destructive hover:bg-destructive/10 flex items-center justify-center"
                                    aria-label="Remove"
                                >
                                    <Trash2 size={15} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {!adding ? (
                    <div className="mt-5">
                        <button
                            data-testid="payouts-add-btn"
                            onClick={() => setAdding(true)}
                            className="w-full flex items-center justify-center gap-2 h-14 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 text-primary font-medium text-[13.5px] active:scale-[0.98]"
                        >
                            <Plus size={16} />
                            Add Bank Account
                        </button>
                    </div>
                ) : (
                    <AddBankForm
                        onCancel={() => setAdding(false)}
                        onAdded={() => setAdding(false)}
                        userId={user.id}
                    />
                )}

                <div className="h-6" />
            </div>
        </div>
    );
};

const AddBankForm = ({ onCancel, onAdded, userId }) => {
    const [bankId, setBankId] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [lookup, setLookup] = useState({ loading: false, name: null, error: null });

    const bank = NG_BANKS.find((b) => b.id === bankId);

    const runLookup = async (n) => {
        setLookup({ loading: true, name: null, error: null });
        const res = await mockAccountNameLookup(n);
        if (res.ok) setLookup({ loading: false, name: res.name, error: null });
        else setLookup({ loading: false, name: null, error: res.error });
    };

    const onAccChange = (v) => {
        const clean = v.replace(/\D/g, "").slice(0, 10);
        setAccountNumber(clean);
        if (clean.length === 10) runLookup(clean);
        else setLookup({ loading: false, name: null, error: null });
    };

    const canSave = bankId && accountNumber.length === 10 && lookup.name;

    const handleSave = () => {
        if (!canSave) return;
        addPayoutMethod(userId, {
            bankId,
            bankName: bank.name,
            bankColor: bank.color,
            accountNumber,
            accountName: lookup.name,
        });
        onAdded();
    };

    return (
        <div className="mt-5 p-4 rounded-2xl border border-border bg-card" data-testid="payouts-add-form">
            <h3 className="font-display text-[15px] font-semibold mb-3">Add bank account</h3>
            <label className="block mb-3">
                <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5">Bank</span>
                <select
                    data-testid="payouts-bank-select"
                    value={bankId}
                    onChange={(e) => setBankId(e.target.value)}
                    className="w-full h-12 px-3 rounded-xl border border-border bg-secondary outline-none text-[14px]"
                >
                    <option value="">Choose a bank</option>
                    {NG_BANKS.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                </select>
            </label>
            <label className="block mb-3">
                <span className="block text-[11.5px] font-medium text-muted-foreground mb-1.5">
                    Account number
                </span>
                <input
                    data-testid="payouts-account-number"
                    value={accountNumber}
                    onChange={(e) => onAccChange(e.target.value)}
                    inputMode="numeric"
                    placeholder="10-digit account number"
                    className="w-full h-12 px-3 rounded-xl border border-border bg-secondary outline-none text-[14px]"
                />
            </label>

            <div className="rounded-xl bg-secondary px-3 py-2.5 text-[12.5px]" data-testid="payouts-lookup">
                {lookup.loading ? (
                    <span className="text-muted-foreground">Verifying account…</span>
                ) : lookup.error ? (
                    <span className="text-destructive">{lookup.error}</span>
                ) : lookup.name ? (
                    <span className="inline-flex items-center gap-1.5 text-primary font-semibold">
                        <Check size={14} /> {lookup.name}
                    </span>
                ) : (
                    <span className="text-muted-foreground">Enter a 10-digit account number to verify.</span>
                )}
            </div>

            <div className="mt-4 flex items-center gap-2">
                <button
                    data-testid="payouts-cancel"
                    onClick={onCancel}
                    className="flex-1 h-12 rounded-2xl bg-secondary text-secondary-foreground font-medium text-[13.5px]"
                >
                    Cancel
                </button>
                <div className="flex-1">
                    <GradientButton testId="payouts-save" onClick={handleSave} disabled={!canSave} tall={false}>
                        Save
                    </GradientButton>
                </div>
            </div>
        </div>
    );
};

export default PayoutMethods;
